import { useEffect, useState } from 'react';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';

/** `@handle` anywhere in a draft, the same shape the server turns into a Slack notice. */
const MENTION = /@([A-Za-z0-9._-]{2,64})/g;

/** The handles a draft names, lowercased, each once, in order of first appearance. */
export function mentionsIn(text: string): string[] {
  return [
    ...new Set(
      [...text.matchAll(MENTION)].map((match) => (match[1] ?? '').toLowerCase()).filter((handle) => handle.length > 0),
    ),
  ];
}

/**
 * The names a draft mentions that the roster does not know (SSD-7435). The signed-in operator is always known.
 * Without a roster there is nothing to check against, so nothing is flagged: a guess would warn about everyone.
 */
export function unknownMentions(text: string, known: ReadonlySet<string> | null, self?: string): string[] {
  if (!known) {
    return [];
  }
  return mentionsIn(text).filter((handle) => !known.has(handle) && handle !== self?.toLowerCase());
}

/** How long one roster read serves every composer on the page; the server caches for the same span. */
const ROSTER_TTL_MS = 5 * 60_000;

let cached: { known: ReadonlySet<string>; readAt: number } | null = null;
let inFlight: Promise<ReadonlySet<string> | null> | null = null;

/** Test seam: forget the roster so the next composer reads it again. */
export function resetMentionRoster(): void {
  cached = null;
  inFlight = null;
}

function fresh(): ReadonlySet<string> | null {
  return cached && Date.now() - cached.readAt < ROSTER_TTL_MS ? cached.known : null;
}

function readRoster(): Promise<ReadonlySet<string> | null> {
  const known = fresh();
  if (known) {
    return Promise.resolve(known);
  }
  if (!inFlight) {
    const client = new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT));
    inFlight = client
      .listMentionHandles()
      .then((response) => {
        const handles: ReadonlySet<string> = new Set(response.handles.map((handle) => handle.toLowerCase()));
        cached = { known: handles, readAt: Date.now() };
        return handles;
      })
      // No roster means no warning rather than a wrong one; the next composer to mount tries again.
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** The handles the admin knows people by, or null until they are read (and when the read failed). */
export function useMentionRoster(): ReadonlySet<string> | null {
  const [known, setKnown] = useState<ReadonlySet<string> | null>(fresh);
  useEffect(() => {
    let ignore = false;
    void readRoster().then((roster) => {
      if (!ignore) {
        setKnown(roster);
      }
    });
    return () => {
      ignore = true;
    };
  }, []);
  return known;
}
